import { Button } from "../components/rumonai";
import { LightMotion } from "../components/LightMotion";
import { useEffect, useState } from "react";
import { ApiError } from "../lib/api";
import { Icon } from "../components/Icon";
import { navigateWorkbench } from "../lib/studioNavigation";
import { AuthShell, PasswordField, SubmitButton } from "./AuthShell";
import { announceAccountChange, hub, type Account } from "./api";

/**
 * The two pages a mailed link opens: `#/reset/<token>` and `#/confirm-email/<token>`.
 *
 * The token sits after the `#`, so the browser never sends it anywhere until one
 * of these pages posts it in a body. Each page asks about its token once per
 * load, however often it renders: a confirmation is spent by being looked at,
 * and a second look would report a link that had just worked as used.
 */
const asked = new Map<string, Promise<unknown>>();
function askOnce<T>(key: string, ask: () => Promise<T>): Promise<T> {
  let held = asked.get(key) as Promise<T> | undefined;
  if (!held) {
    held = ask();
    asked.set(key, held);
    // A failure to reach the Hub is worth asking again; an answer is not.
    held.catch(failure => { if (!(failure instanceof ApiError) || failure.status >= 500) asked.delete(key); });
  }
  return held;
}

const enter = () => navigateWorkbench("#/", true);

function Waiting({ children }: { children: string }) {
  return <p className="auth-waiting pa-reveal" role="status"><LightMotion state="working" compact />{children}</p>;
}

/** What a link that cannot be used says, and the next step: the page is never a dead end. */
function Gone({ error, actions, hint = true }: { error: ApiError; actions: React.ReactNode; hint?: boolean }) {
  return <div className="auth-gone pa-reveal" role="alert">
    <p>{error.detail}</p>
    {hint && error.hint ? <small>{error.hint}</small> : null}
    <div className="auth-gone-actions">{actions}</div>
  </div>;
}

export function ResetByLink({ token, account, onAccount }: {
  token: string;
  /** Whoever is signed in on this browser, if anyone: the link signs this browser in as the account it resets. */
  account: Account | null;
  onAccount: (account: Account) => void;
}) {
  const [link, setLink] = useState<{ state: "checking" } | { state: "ready"; name: string } | { state: "gone"; error: ApiError }>(
    { state: "checking" });
  const [password, setPassword] = useState("");
  const [hidden, setHidden] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  useEffect(() => {
    let live = true;
    setLink({ state: "checking" });
    askOnce(`reset:${token}`, () => hub.checkResetLink(token)).then(
      found => { if (live) setLink({ state: "ready", name: found.name }); },
      failure => { if (live) setLink({ state: "gone", error: ApiError.fromUnknown(failure) }); },
    );
    return () => { live = false; };
  }, [token]);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy || password.length < 8) return;
    setBusy(true);
    setError(null);
    try {
      const result = await hub.recoverPassword(token, password);
      asked.delete(`reset:${token}`);
      setPassword("");
      announceAccountChange();
      onAccount(result.account);
      enter();
    } catch (failure) {
      const refusal = ApiError.fromUnknown(failure);
      if (refusal.status === 410) setLink({ state: "gone", error: refusal });
      else setError(refusal);
    } finally {
      setBusy(false);
    }
  };
  if (link.state === "gone") {
    return <AuthShell title="链接不能用了">
      {/* The Hub's hint sends somebody to the sign-in page; somebody already signed in has no use for it. */}
      <Gone error={link.error} hint={!account} actions={account
        ? <Button className="auth-submit" type="button" onClick={enter}><span>回到涟漪</span><Icon name="arrow" size={18} /></Button>
        : <>
          <Button className="auth-submit" type="button" onClick={() => navigateWorkbench("#/forgot", true)}>
            <span>重新申请重置链接</span><Icon name="arrow" size={18} /></Button>
          <Button variant="ghost" className="auth-quiet" type="button" onClick={enter}>返回登录</Button>
        </>} />
    </AuthShell>;
  }
  if (link.state === "checking") {
    return <AuthShell title="设置新密码"><Waiting>正在检查链接…</Waiting></AuthShell>;
  }
  return <AuthShell title="设置新密码" lead={`为「${link.name}」设置新密码，然后直接进入。`}>
    <form className="auth-form pa-reveal" onSubmit={event => { void submit(event); }} aria-busy={busy} data-mode="recover" noValidate>
      {/* For the browser's password manager: whose password this is. */}
      <input type="text" name="username" autoComplete="username" value={link.name} readOnly hidden />
      <PasswordField label="新密码" placeholder="至少 8 位" value={password} onChange={setPassword}
        autoComplete="new-password" setting disabled={busy} hidden={hidden} onHide={setHidden} />
      <div className="auth-form-actions">
        <div className="auth-feedback" role="alert" aria-atomic="true">
          {error ? <div className="pa-feedback"><strong>{error.title}</strong><p>{error.detail}{error.hint ? ` ${error.hint}` : ""}</p></div> : null}
        </div>
        <SubmitButton busy={busy} doing="正在设置…" disabled={password.length < 8}>设置新密码并进入</SubmitButton>
      </div>
    </form>
  </AuthShell>;
}

export function ConfirmEmail({ token }: { token: string }) {
  const [result, setResult] = useState<{ state: "working" } | { state: "done"; email: string | null } | { state: "failed"; error: ApiError }>(
    { state: "working" });
  useEffect(() => {
    let live = true;
    askOnce(`confirm:${token}`, () => hub.confirmEmail(token)).then(
      done => { if (live) setResult({ state: "done", email: done.email }); },
      failure => { if (live) setResult({ state: "failed", error: ApiError.fromUnknown(failure) }); },
    );
    return () => { live = false; };
  }, [token]);
  if (result.state === "working") return <AuthShell title="正在确认邮箱"><Waiting>请稍等…</Waiting></AuthShell>;
  if (result.state === "failed") {
    return <AuthShell title="没有确认成功">
      <Gone error={result.error} actions={
        <Button className="auth-submit" type="button" onClick={enter}><span>进入涟漪</span><Icon name="arrow" size={18} /></Button>} />
    </AuthShell>;
  }
  return <AuthShell title="邮箱已确认" lead={result.email ? `以后忘了密码，可以用 ${result.email} 自己重设。` : "以后忘了密码，可以自己重设。"}>
    <div className="auth-gone pa-reveal">
      <div className="auth-gone-actions">
        <Button className="auth-submit" type="button" onClick={enter}><span>进入涟漪</span><Icon name="arrow" size={18} /></Button>
      </div>
    </div>
  </AuthShell>;
}
